import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { EScrollDirection } from 'common/const';
import { InfiniteScroll, Spin } from 'common/components';
import { DEFAULT_PAGINATION_PAGE, LIST_LIMIT_20 } from 'common/config';
import { AuthHooks } from 'common/hooks';
import { parseFilters } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  CatalogCategoryList,
  CatalogGoodsListFilters,
  CatalogGoodsTable,
  CatalogHeader,
  CatalogNavigation,
  CatalogTitle,
} from 'entities/Catalog/components';
import { getCatalogGoodsAvailabilityFilters } from 'entities/Catalog/Catalog.helper';
import { ICatalog } from 'entities/Catalog/Catalog.models';
import { ICategoryListParams } from 'entities/Category/Category.models';
import { IGoodListPayload, IGoodPosition } from 'entities/Goods/Goods.models';
import {
  getCategoriesGoodsCountItems,
  getCategoryById,
  getCategoryList,
  selectCategoryList,
} from 'entities/Category/Category.slice';
import { getGoodList, getGoodListChunk, selectGoodHasMore, selectIsGoodLoading } from 'entities/Goods/Good.slice';
import { getPropertyListByCategory } from 'entities/Property/Property.slice';
import { selectCurrentUser } from 'entities/User/User.slice';
import { IPropertyCollection } from 'entities/Property/Property.models';

interface IComponentProps {
  catalog: ICatalog;
  categoryId?: number;
}

export const CatalogCategoryPage: FC<IComponentProps> = ({ catalog, categoryId }) => {
  const dispatch = useAppDispatch();
  // category
  const categoryList = useAppSelector(selectCategoryList);
  // good
  const goodHasMore = useAppSelector(selectGoodHasMore);
  const isGoodLoading = useAppSelector(selectIsGoodLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [categoryListParams, setCategoryListParams] = useState<ICategoryListParams | null>(null);
  const [goodListPayload, setGoodListPayload] = useState<IGoodListPayload>({});
  const [styles, setStyles] = useState<Record<string, React.CSSProperties>>({
    filters: { top: 0 },
    tableHeader: { top: 0 },
  });
  const filtersContainerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initializedRef = useRef(false);
  const { isSeller } = AuthHooks.useIsSeller();

  const search = searchParams.get('search');

  const positions = useMemo((): IGoodPosition[] => {
    const filters = parseFilters(searchParams);

    return Object.entries(filters).map(([key, filterValue]) => {
      if (filterValue && typeof filterValue === 'object' && !Array.isArray(filterValue) && filterValue.t === 'r') {
        return { propertyId: Number(key), range: { from: filterValue.from, to: filterValue.to } } as IGoodPosition;
      }

      return { propertyId: Number(key), values: filterValue };
    });
  }, [searchParams]);

  const fetchPropertyList = (payload: IGoodListPayload): Promise<IPropertyCollection | null> => {
    return dispatch(getPropertyListByCategory({ ...payload, limit: 0, offset: undefined }));
  };

  useEffect(() => {
    initializedRef.current = false;

    const fetch = async () => {
      setFetchLoading(true);
      setPage(DEFAULT_PAGINATION_PAGE);

      const { isAvailableForCustomer, isAvailableForPurchase } = getCatalogGoodsAvailabilityFilters(
        location,
        isSeller,
        currentUser?.account,
      );

      const categoryListParams: ICategoryListParams = {
        catalogId: catalog.id,
        isAvailableForCustomer,
        withoutParents: false,
        limit: 0,
      };

      if (categoryId) {
        categoryListParams.parentId = categoryId;
        await dispatch(getCategoryById(categoryId));
      }

      if (search) {
        categoryListParams.search = search;
      }

      setCategoryListParams(categoryListParams);
      const categoryList = await dispatch(getCategoryList(categoryListParams));

      if (categoryList) {
        const categoryIds = categoryList.map((item) => item.id);

        await dispatch(
          getCategoriesGoodsCountItems({
            catalogId: catalog.id,
            categoryIds,
            isAvailableForCustomer,
            isAvailableForPurchase,
          }),
        );
      }

      const goodListPayload: IGoodListPayload = {
        catalogId: catalog.id,
        isAvailableForCustomer,
        isAvailableForPurchase,
        positions,
        limit: LIST_LIMIT_20,
        offset: 0,
      };

      if (categoryId) {
        goodListPayload.categoryId = categoryId;

        await fetchPropertyList(goodListPayload);
      }

      if (search) {
        goodListPayload.search = search;
      }

      setGoodListPayload(goodListPayload);
      await dispatch(getGoodList(goodListPayload));

      initializedRef.current = true;
      setFetchLoading(false);
    };

    fetch();
  }, [catalog, categoryId, search, currentUser?.city?.id]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    const newGoodListPayload = { ...goodListPayload, positions, offset: 0 };

    setPage(DEFAULT_PAGINATION_PAGE);
    setGoodListPayload(newGoodListPayload);

    if (newGoodListPayload.categoryId) {
      fetchPropertyList(newGoodListPayload);
    }

    dispatch(getGoodList(newGoodListPayload));
  }, [positions]);

  useEffect(() => {
    const filtersContainer = filtersContainerRef.current;

    if (!filtersContainer) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      requestAnimationFrame(() => {
        setStyles({
          filters: { top: 0 },
          tableHeader: { top: entry.target.getBoundingClientRect().height },
        });
      });
    });

    observer.observe(filtersContainer);

    return () => observer.disconnect();
  }, [filtersContainerRef.current]);

  const handleScroll = (direction?: string) => {
    const filtersContainerClientHeight = filtersContainerRef.current?.clientHeight ?? 0;

    if (direction === EScrollDirection.UP) {
      setStyles({
        filters: { top: 0 },
        tableHeader: { top: filtersContainerClientHeight },
      });
    } else if (direction === EScrollDirection.DOWN) {
      setStyles({
        filters: { top: -filtersContainerClientHeight },
        tableHeader: { top: 0 },
      });
    }
  };

  const onAvailableForCustomerFilterChange = async (value: boolean) => {
    const newGoodListPayload = {
      ...goodListPayload,
      offset: 0,
      isAvailableForCustomer: value,
      isAvailableForPurchase: value ? true : undefined,
    };

    if (categoryListParams) {
      const categories = await dispatch(
        getCategoryList({
          ...categoryListParams,
          isAvailableForCustomer: newGoodListPayload.isAvailableForCustomer,
        }),
      );

      if (categories) {
        const categoryIds = categories.map((item) => item.id);

        await dispatch(
          getCategoriesGoodsCountItems({
            catalogId: catalog.id,
            categoryIds,
            isAvailableForCustomer: newGoodListPayload.isAvailableForCustomer,
            isAvailableForPurchase: newGoodListPayload.isAvailableForPurchase,
          }),
        );
      }
    }

    navigate(location, {
      replace: true,
      state: {
        isAvailableForCustomer: newGoodListPayload.isAvailableForCustomer,
        isAvailableForPurchase: newGoodListPayload.isAvailableForPurchase,
      },
    });
    setPage(DEFAULT_PAGINATION_PAGE);
    setGoodListPayload(newGoodListPayload);

    if (newGoodListPayload.categoryId) {
      await fetchPropertyList(newGoodListPayload);
    }

    dispatch(getGoodList(newGoodListPayload));
  };

  const onAvailableForPurchaseFilterChange = async (value: boolean) => {
    // «Доступен к покупке» ⊂ «доступен покупателю» → форсим isAvailableForCustomer=true и в листинге,
    // и в счётчике, и в state — иначе счётчик и список расходятся (счётчик считал бы по true, список по старому).
    const newGoodListPayload = {
      ...goodListPayload,
      offset: 0,
      isAvailableForCustomer: true,
      isAvailableForPurchase: value ? true : undefined,
    };

    const categoryIds = categoryList.map((item) => item.id);

    await dispatch(
      getCategoriesGoodsCountItems({
        catalogId: catalog.id,
        categoryIds,
        isAvailableForCustomer: newGoodListPayload.isAvailableForCustomer,
        isAvailableForPurchase: newGoodListPayload.isAvailableForPurchase,
      }),
    );

    navigate(location, {
      replace: true,
      state: {
        isAvailableForCustomer: true,
        isAvailableForPurchase: newGoodListPayload.isAvailableForPurchase,
      },
    });
    setPage(DEFAULT_PAGINATION_PAGE);
    setGoodListPayload(newGoodListPayload);

    if (newGoodListPayload.categoryId) {
      await fetchPropertyList(newGoodListPayload);
    }

    dispatch(getGoodList(newGoodListPayload));
  };

  const onClearFilters = () => {
    setPage(DEFAULT_PAGINATION_PAGE);
  };

  const handleSortChange = (orderDirection?: string, orderField?: string): void => {
    const newGoodListPayload: IGoodListPayload = {
      ...goodListPayload,
      offset: 0,
      // @ts-ignore
      orderDirection,
      // @ts-ignore
      orderField,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setGoodListPayload(newGoodListPayload);
    dispatch(getGoodList(newGoodListPayload));
  };

  return (
    <div className="redesign relative pt-11 px-11 h-full flex direction-column">
      <CatalogHeader catalog={catalog} />

      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isGoodLoading && goodHasMore}
        loader={<Spin />}
        scrollToTopTrigger={[goodListPayload]}
        loadMore={async () => {
          setScrollLoading(true);
          if (goodListPayload) {
            setPage(page + 1);
            await dispatch(
              getGoodListChunk({
                ...goodListPayload,
                offset: LIST_LIMIT_20 * page,
              }),
            );
          }
          setScrollLoading(false);
        }}
        onScroll={handleScroll}
      >
        <div className="mx-11 flex direction-column flex-1">
          <CatalogNavigation catalog={catalog} />

          <CatalogTitle catalogName={catalog.name} />

          <div className="catalog-goods-list-filters__wrapper" style={{ top: styles.filters.top }}>
            <div ref={filtersContainerRef} className="pb-13">
              <CatalogGoodsListFilters
                categoryId={categoryId}
                isAvailableForCustomer={goodListPayload.isAvailableForCustomer}
                isAvailableForPurchase={goodListPayload.isAvailableForPurchase}
                positions={positions}
                onAvailableForCustomerChange={onAvailableForCustomerFilterChange}
                onAvailableForPurchaseChange={onAvailableForPurchaseFilterChange}
                onClear={onClearFilters}
              />
            </div>
          </div>

          <CatalogCategoryList catalogId={catalog.id} />

          <CatalogGoodsTable
            styles={styles}
            catalogId={catalog.id}
            categoryId={categoryId}
            loading={fetchLoading}
            payload={goodListPayload}
            onShowAllAssortmentClick={() => onAvailableForCustomerFilterChange(false)}
            onSort={handleSortChange}
          />
        </div>
      </InfiniteScroll>
    </div>
  );
};
